import React from "react";
import { UnifiedSelect } from "@cmp-src/form/UnifiedSelect/UnifiedSelect";
import { SelectItem } from "@cmp-src/form/UnifiedSelect/types";
import { Chip } from "@cmp-src/data-display/chips/Chip/Chip";
import { TagsInputProps, TagElement } from "./TagsInput.types";
import { FormControlWrapper } from "@cmp-src/form/FormControlWrapper";

/**
 * Picks several tags from a fixed list: a searchable multiple-choice `UnifiedSelect` with the
 * chosen tags shown as removable chips under it (a tag's `chip` renders its own, e.g. a
 * `UserChip`). The value is the list of tag values. Inside react-hook-form use
 * `HookFormTagsInput`.
 */
export const TagsInput = React.forwardRef<HTMLInputElement, TagsInputProps>(
  (
    {
      selectedTags: controlledValue,
      onChangeValue,
      tags,
      noItemsFoundMessage = "No items found",
      label,
      placeholder = "Search...",
      disabled,
      error,
      ...rest
    },
    ref
  ) => {
    const [uncontrolledSelectedItems, setSelectedItems] = React.useState<
      string[]
    >([]);

    const selectedItems =
      controlledValue !== undefined
        ? controlledValue
        : uncontrolledSelectedItems;

    // Convert TagElement[] to SelectItem[]
    const items: SelectItem[] = React.useMemo(
      () =>
        tags.map((tag) => ({
          id: tag.value,
          name: typeof tag.label === "string" ? tag.label : tag.value,
          keywords: tag.keywords
        })),
      [tags]
    );

    // Convert selected string[] to SelectItem[]
    const selectedValues: SelectItem[] = React.useMemo(
      () =>
        selectedItems
          .map((id) => items.find((item) => item.id === id))
          .filter((item): item is SelectItem => !!item),
      [selectedItems, items]
    );

    const handleSelectMultiple = (newItems: SelectItem[]) => {
      const newValues = newItems.map((item) => item.id);
      if (controlledValue === undefined) {
        setSelectedItems(newValues);
      }
      onChangeValue?.(newValues);
    };

    const handleRemoveTag = (tagId: string) => {
      const newValues = selectedItems.filter((id) => id !== tagId);
      if (controlledValue === undefined) {
        setSelectedItems(newValues);
      }
      onChangeValue?.(newValues);
    };

    // Find the tag element for custom chip rendering
    const getTagElement = (id: string): TagElement | undefined =>
      tags.find((tag) => tag.value === id);

    return (
      <FormControlWrapper
        variant={"unstyled"}
        className={"w-full"}
        label={label}
        error={error}
        {...rest}
      >
        <div className={"w-full"}>
          <UnifiedSelect
            items={items}
            values={selectedValues}
            onSelectMultiple={handleSelectMultiple}
            multiselect
            searchable
            searchPlaceholder={placeholder}
            disabled={disabled}
            placeholder={placeholder}
            label={typeof label === "string" ? label : undefined}
            triggerClassName="w-full"
            renderValue={() => (
              <span className="text-content">{placeholder}</span>
            )}
          />
          {selectedItems.length > 0 && (
            <div className="mx-[-2px] mt-2 flex w-full flex-wrap">
              {selectedItems.map((id) => {
                const tag = getTagElement(id);
                if (!tag) return null;

                const onCancel = () => handleRemoveTag(id);

                const chipProps = {
                  className: "m-1",
                  onCancel,
                  onClick: onCancel
                };

                return tag.chip ? (
                  <React.Fragment key={id}>
                    {tag.chip(chipProps)}
                  </React.Fragment>
                ) : (
                  <Chip key={id} {...chipProps}>
                    {tag.label}
                  </Chip>
                );
              })}
            </div>
          )}
        </div>
      </FormControlWrapper>
    );
  }
);

TagsInput.displayName = "TagsInput";
